import { useState, useEffect, useCallback } from 'react';
import {
  ScrollText, RefreshCw, Search, AlertTriangle, XCircle, Flame,
  ChevronDown, ChevronRight, Copy, Check,
} from 'lucide-react';

import { systemLogsApi } from '@/api/systemLogs.api';
import type { AppLogDto } from '@/api/systemLogs.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { Spinner }       from '@/components/ui/Spinner';
import { cn }            from '@/utils/cn';

const LEVELS = ['All', 'Warning', 'Error', 'Fatal'] as const;

const levelStyle = (level: string) => {
  switch (level) {
    case 'Fatal':   return 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300';
    case 'Error':   return 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300';
    case 'Warning': return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
    default:        return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
  }
};

const LevelIcon = ({ level }: { level: string }) => {
  if (level === 'Fatal') return <Flame size={12} />;
  if (level === 'Error') return <XCircle size={12} />;
  return <AlertTriangle size={12} />;
};

const fmt = (iso: string) => new Date(iso).toLocaleString(undefined, {
  dateStyle: 'medium', timeStyle: 'medium',
});

// ── One row, expandable to show the stack trace ──────────────────────────────
const LogRow = ({ log }: { log: AppLogDto }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyCorrelation = async () => {
    if (!log.correlationId) return;
    try {
      await navigator.clipboard.writeText(log.correlationId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — not worth surfacing */ }
  };

  return (
    <div className="border-b border-gray-100 dark:border-gray-800 last:border-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-start gap-3 px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <span className="mt-0.5 text-gray-400 flex-shrink-0">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>

        <span className={cn(
          'flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase flex-shrink-0 mt-0.5',
          levelStyle(log.level),
        )}>
          <LevelIcon level={log.level} /> {log.level}
        </span>

        <span className="text-[11px] text-gray-500 dark:text-gray-400 font-mono flex-shrink-0 mt-1 w-40">
          {fmt(log.loggedAt)}
        </span>

        <span className="flex-1 min-w-0 text-sm text-gray-800 dark:text-gray-200 break-words">
          {log.message}
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 pl-12 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[11px]">
            {log.correlationId && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500 dark:text-gray-400">Correlation ID</span>
                <code className="font-mono text-gray-800 dark:text-gray-200">{log.correlationId}</code>
                <button onClick={copyCorrelation} title="Copy" className="text-gray-400 hover:text-brand-600">
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
            )}
            {log.requestPath && (
              <div><span className="text-gray-500 dark:text-gray-400">Request </span>
                <code className="font-mono text-gray-800 dark:text-gray-200">{log.requestPath}</code></div>
            )}
            {log.sourceContext && (
              <div className="sm:col-span-2 truncate">
                <span className="text-gray-500 dark:text-gray-400">Source </span>
                <code className="font-mono text-gray-700 dark:text-gray-300">{log.sourceContext}</code></div>
            )}
            {log.machineName && (
              <div><span className="text-gray-500 dark:text-gray-400">Machine </span>
                <code className="font-mono text-gray-700 dark:text-gray-300">{log.machineName}</code></div>
            )}
          </div>

          {log.exception && (
            <pre className="overflow-x-auto rounded-lg bg-gray-900 dark:bg-black text-gray-100 text-[11px] leading-relaxed p-3 font-mono">
              {log.exception}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────
export const SystemLogsPage = () => {
  const { hasPermission } = usePermission();
  const canView = hasPermission(Permissions.SYSTEM_LOG_VIEW);

  const [logs,    setLogs]    = useState<AppLogDto[]>([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(true);
  const [level,   setLevel]   = useState<string>('All');
  const [search,  setSearch]  = useState('');
  const [applied, setApplied] = useState('');

  const pageSize = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await systemLogsApi.getList({
        level: level === 'All' ? undefined : level,
        search: applied || undefined,
        pageNumber: page,
        pageSize,
      });
      const d = res.data.data;
      setLogs(d?.items ?? []);
      setTotal(d?.totalCount ?? 0);
    } catch {
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [level, applied, page]);

  useEffect(() => { if (canView) load(); }, [canView, load]);

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        You do not have permission to view system logs.
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
            <ScrollText size={20} className="text-slate-600 dark:text-slate-300" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">System Logs</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Warnings and errors raised by the application — {total.toLocaleString()} entries.
            </p>
          </div>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50">
          <RefreshCw size={14} className={cn(loading && 'animate-spin')} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
          {LEVELS.map(l => (
            <button key={l}
              onClick={() => { setLevel(l); setPage(1); }}
              className={cn(
                'px-3 py-1.5 text-xs font-medium transition-colors',
                level === l
                  ? 'bg-brand-600 text-white'
                  : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
              )}>
              {l}
            </button>
          ))}
        </div>

        <div className="relative flex-1 min-w-56">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setApplied(search.trim()); setPage(1); } }}
            placeholder="Search message or stack trace, then press Enter…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
      </div>

      {/* List */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40"><Spinner /></div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 gap-2 text-gray-500 dark:text-gray-400">
            <ScrollText size={30} className="text-gray-300 dark:text-gray-600" />
            <p className="text-sm">No log entries match those filters.</p>
            <p className="text-xs">Only warnings and above are recorded here.</p>
          </div>
        ) : (
          logs.map(l => <LogRow key={l.id} log={l} />)
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800/50">
              Previous
            </button>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800/50">
              Next
            </button>
          </div>
        </div>
      )}

      <p className="text-[11px] text-gray-400 dark:text-gray-500">
        Request-level (Information) logging is kept in rolling files on the server, not here — this
        view is deliberately limited to warnings and above so it stays readable.
      </p>
    </div>
  );
};

export default SystemLogsPage;
